import { CheckCircle, Television, WarningCircle } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import PhoneInput from "../../../components/SearchablePhoneInput";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { formatAsMoney } from "../../../helpers/numbers";
import { getFriendlyBillsErrorMessage } from "../../../helpers/billsErrors";
import { BillNetwork } from "../../../hooks/useBillAirtimeProviders";
import useBillCableProviders from "../../../hooks/useBillCableProviders";
import useBillCableVerify, {
  BillCablePlan,
} from "../../../hooks/useBillCableVerify";
import useBillCablePurchase, {
  BillCablePurchasePayload,
} from "../../../hooks/useBillCablePurchase";
import BillPurchaseSuccess from "./BillPurchaseSuccess";
import Confirm from "../Confirm";
import BillPageHeader from "./components/BillPageHeader";
import SelectField from "./components/SelectField";
import ProviderSelectModal from "./components/ProviderSelectModal";
import PlanSelectModal, {
  BillPlanOption,
} from "./components/PlanSelectModal";
import BillSummaryCard from "./components/BillSummaryCard";
import SetPinPrompt from "./components/SetPinPrompt";

const WALLET_PAYMENT_OPTION_CODE = "004";

const toNumber = (value: unknown) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value.replace(/[^\d.-]/g, "")) || 0;
  return 0;
};

const generateAgentReference = () =>
  `tv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const planCodeOf = (p: BillCablePlan) => String(p.code ?? p.plan_code ?? "");
const planNameOf = (p: BillCablePlan) => String(p.name ?? p.plan_name ?? "");
const planAmountOf = (p: BillCablePlan) => toNumber(p.price ?? p.amount ?? 0);

const CableTvTemp = () => {
  const { user, refetch } = useAuth();

  const [selectedProvider, setSelectedProvider] = useState<BillNetwork | null>(
    null
  );
  const [smartcard, setSmartcard] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [selectedPlan, setSelectedPlan] = useState<BillCablePlan | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [pin, setPin] = useState("");
  const [payload, setPayload] = useState<BillCablePurchasePayload | null>(null);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  // Verified smartcard (name + plans). Cleared when the provider/smartcard changes.
  const [verified, setVerified] = useState<{
    name: string;
    plans: BillCablePlan[];
    smartcard: string;
    provider: string;
  } | null>(null);

  const { providers, fetchingProviders, isProvidersError, providersError } =
    useBillCableProviders();

  const { verifySmartcard, verifyingSmartcard } = useBillCableVerify({
    onSuccess(data) {
      setVerified({
        name: data?.data?.customer_name || "Verified customer",
        plans: data?.data?.plans || [],
        smartcard,
        provider: selectedProvider?.code || "",
      });
    },
    onError(error) {
      setVerified(null);
      toast.error(getFriendlyBillsErrorMessage(error, "cable-tv"));
    },
  });

  const {
    purchaseCable,
    purchasingCable,
    isPurchaseError,
    purchaseError,
    purchaseResponse,
  } = useBillCablePurchase({
    onSuccess(data) {
      toast.success(data?.message || "Cable TV subscription successful");
      refetch();
      setConfirming(false);
    },
    onError(error) {
      toast.error(getFriendlyBillsErrorMessage(error, "cable-tv"));
    },
  });

  const availableBalance = toNumber(user?.wallet?.available_balance);
  const isVerified =
    !!verified &&
    verified.smartcard === smartcard &&
    verified.provider === (selectedProvider?.code || "");
  const planAmount = selectedPlan ? planAmountOf(selectedPlan) : 0;
  const hasEnoughBalance = availableBalance >= planAmount;

  const canVerify =
    !!selectedProvider &&
    smartcard.trim().length > 3 &&
    !!phoneNumber &&
    !verifyingSmartcard;
  const canProceed =
    isVerified &&
    !!selectedPlan &&
    planAmount > 0 &&
    hasEnoughBalance &&
    !purchasingCable;

  const plans = useMemo(() => (isVerified ? verified?.plans || [] : []), [
    isVerified,
    verified,
  ]);

  const planOptions = useMemo<BillPlanOption[]>(
    () =>
      plans.map((plan) => ({
        id: planCodeOf(plan),
        name: planNameOf(plan),
        amount: planAmountOf(plan),
      })),
    [plans]
  );

  const clearVerified = () => {
    setVerified(null);
    setSelectedPlan(null);
  };

  const handleVerify = () => {
    if (!selectedProvider || !smartcard || !phoneNumber) {
      toast.error("Select a provider, enter your smartcard number and phone.");
      return;
    }
    verifySmartcard({
      service_type: selectedProvider.code,
      smartcard_number: smartcard,
      phone: phoneNumber,
    });
  };

  const handlePlanSelect = (id: string) => {
    setSelectedPlan(plans.find((p) => planCodeOf(p) === id) || null);
  };

  const handleSubmit = () => {
    if (!user?.pin_status) return;
    if (!isVerified || !selectedPlan) {
      toast.error("Verify your smartcard and select a plan first.");
      return;
    }
    if (!hasEnoughBalance) {
      toast.error("Insufficient wallet balance.");
      return;
    }

    setPayload({
      pin: "",
      service_type: selectedProvider!.code,
      smartcard_number: smartcard,
      phone: phoneNumber!,
      amount: planAmount,
      plan_code: planCodeOf(selectedPlan),
      plan_name: planNameOf(selectedPlan),
      payment_option_code: WALLET_PAYMENT_OPTION_CODE,
      agentReference: generateAgentReference(),
    });
    setConfirming(true);
  };

  const authorizePurchase = useCallback(() => {
    if (!payload || !pin) return;
    purchaseCable({ ...payload, pin });
  }, [payload, pin, purchaseCable]);

  if (!user?.pin_status) {
    return (
      <SetPinPrompt
        redirect="/payments/bills/cable-tv"
        action="paying for cable TV"
      />
    );
  }

  return (
    <div className="relative min-h-[70vh] px-5 py-8">
      {(fetchingProviders || purchasingCable) && (
        <div className="fixed top-0 z-[120] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {purchaseResponse && (
        <BillPurchaseSuccess
          title="Cable TV subscription successful!"
          message={
            <>
              You have successfully subscribed{" "}
              <span className="font-bold">{planNameOf(selectedPlan || {})}</span>{" "}
              on smartcard <span className="font-bold">{smartcard}</span>.
            </>
          }
        />
      )}

      {confirming && selectedProvider && selectedPlan && (
        <Confirm
          title="Confirm Cable TV Purchase"
          setConfirming={setConfirming}
          onAuthorized={authorizePurchase}
          onPinChange={setPin}
          description={
            <div>
              You are about to pay{" "}
              <span className="font-bold">NGN {formatAsMoney(planAmount)}</span>{" "}
              for {planNameOf(selectedPlan)} ({selectedProvider.name}) on
              smartcard <span className="font-bold">{smartcard}</span>.
            </div>
          }
        />
      )}

      <div className="mx-auto max-w-5xl flex flex-col gap-6">
        <BillPageHeader
          title="Cable TV"
          subtitle="Verify your smartcard, then pick a plan."
          icon={Television}
          walletBalance={availableBalance}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <section className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col gap-5">
            <SelectField
              label="Provider"
              placeholder="Select provider"
              icon={Television}
              value={selectedProvider?.name}
              onClick={() => setProviderModalOpen(true)}
              loading={fetchingProviders}
              loadingText="Loading providers..."
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Smartcard / IUC number
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={smartcard}
                  onChange={(e) => {
                    setSmartcard(e.target.value.replace(/[^\d]/g, ""));
                    clearVerified();
                  }}
                  placeholder="Enter smartcard number"
                  className="mt-2 w-full rounded-xl border border-gray-300 shadow-sm px-4 py-3 outline-none focus:outline focus:outline-deepBlue"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Phone number
                </label>
                <PhoneInput
                  placeholder="Enter phone number"
                  international
                  defaultCountry="NG"
                  withCountryCallingCode
                  value={phoneNumber}
                  onChange={(v) => {
                    setPhoneNumber(v);
                    clearVerified();
                  }}
                  className="mt-2 focus:outline-none flex-1 flex items-center rounded-xl py-2 border border-gray-300 shadow-sm px-4 w-full focus-within:outline-deepBlue focus-within:outline"
                />
              </div>
            </div>

            {!isVerified && (
              <button
                type="button"
                onClick={handleVerify}
                disabled={!canVerify}
                className="self-start px-6 py-3 rounded-xl bg-deepBlue text-white font-medium disabled:opacity-50"
              >
                {verifyingSmartcard ? "Verifying..." : "Verify smartcard"}
              </button>
            )}

            {isVerified && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle size={18} weight="fill" />
                <span>{verified?.name}</span>
              </div>
            )}

            {isVerified && (
              <SelectField
                label="Subscription plan"
                placeholder={
                  plans.length === 0
                    ? "No plans returned for this smartcard"
                    : "Select a plan"
                }
                value={
                  selectedPlan
                    ? `${planNameOf(selectedPlan)} — NGN ${formatAsMoney(
                        planAmountOf(selectedPlan)
                      )}`
                    : undefined
                }
                onClick={() => setPlanModalOpen(true)}
                disabled={plans.length === 0}
              />
            )}

            {isProvidersError && (
              <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                {providersError?.message || "Unable to load providers."}
              </div>
            )}
          </section>

          <BillSummaryCard
            rows={[
              { label: "Provider", value: selectedProvider?.name || "Not selected" },
              { label: "Smartcard", value: smartcard || "Not entered" },
              {
                label: "Customer",
                value: isVerified ? verified!.name : "Not verified",
              },
              {
                label: "Plan",
                value: selectedPlan ? planNameOf(selectedPlan) : "Not selected",
              },
            ]}
            amount={planAmount}
            canProceed={canProceed}
            onProceed={handleSubmit}
            alerts={
              <>
                {!!planAmount && !hasEnoughBalance && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm flex gap-2">
                    <WarningCircle size={20} weight="bold" />
                    <span>Your wallet balance is not enough for this plan.</span>
                  </div>
                )}
                {isPurchaseError && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                    {getFriendlyBillsErrorMessage(purchaseError, "cable-tv")}
                  </div>
                )}
              </>
            }
          />
        </div>
      </div>

      <ProviderSelectModal
        open={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        providers={providers}
        selectedCode={selectedProvider?.code}
        onSelect={(provider) => {
          setSelectedProvider(provider);
          clearVerified();
        }}
        label="provider"
        icon={Television}
      />

      <PlanSelectModal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        plans={planOptions}
        selectedId={selectedPlan ? planCodeOf(selectedPlan) : undefined}
        onSelect={handlePlanSelect}
        showTabs={false}
      />
    </div>
  );
};

export default CableTvTemp;
